import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Bed,
  ArrowRight,
  ArrowLeft,
  Users,
  Percent,
  Clock,
  CalendarCheck,
  X
} from 'lucide-react';

import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const PlanningOccupation = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [chambres, setChambres] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedDetailIndex, setSelectedDetailIndex] = useState(0);

  const annee = currentDate.getFullYear();
  const mois = currentDate.getMonth();

  const nomsMois = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const joursSemaine = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const nombreJours = getDaysInMonth(annee, mois);
  const joursMois = Array.from({ length: nombreJours }, (_, i) => i + 1);

  const formatDateLocal = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) {
      return String(date).split('T')[0];
    }
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0')
    ].join('-');
  };

  const getDateString = (jour) => {
    return `${annee}-${String(mois + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
  };

  const formatDateFR = (date) => {
    if (!date) return 'N/A';
    const dateStr = String(date).split('T')[0];
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const fetchDataPlanning = async () => {
    try {
      setLoading(true);
      const moisStr = `${annee}-${String(mois + 1).padStart(2, '0')}`;

      const [chambresRes, planningRes] = await Promise.all([
        API.get('/hebergement/chambres').catch(() => ({ data: [] })),
        API.get(`/hebergement/reservations/planning?mois=${moisStr}`)
      ]);

      const chambresData = chambresRes?.data?.data || chambresRes?.data || [];
      const reservationsData = planningRes?.data?.data || planningRes?.data || [];

      setChambres(Array.isArray(chambresData) ? chambresData : []);
      setReservations(Array.isArray(reservationsData) ? reservationsData : []);
    } catch (err) {
      console.error('Erreur lors du chargement du planning :', err);
      setChambres([]);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataPlanning();
  }, [annee, mois]);

  const moisPrecedent = () => {
    setCurrentDate(new Date(annee, mois - 1, 1));
    setSelectedCell(null);
  };

  const moisSuivant = () => {
    setCurrentDate(new Date(annee, mois + 1, 1));
    setSelectedCell(null);
  };

  const allerAujourdHui = () => {
    setCurrentDate(new Date());
    setSelectedCell(null);
  };

  const reservationConcerneChambre = (reservation, chambreId, chambreNumero) => {
    if (!reservation) return false;
    if (reservation.chambre_id != null && String(reservation.chambre_id) === String(chambreId)) return true;
    if (reservation.chambre_numero != null && String(reservation.chambre_numero) === String(chambreNumero)) return true;
    if (reservation.chambre_id != null && String(reservation.chambre_id) === String(chambreNumero)) return true;

    if (Array.isArray(reservation.lignes)) {
      return reservation.lignes.some((ligne) => {
        if (!ligne) return false;
        return (
          String(ligne.chambre_id) === String(chambreId) ||
          String(ligne.chambre_numero) === String(chambreNumero)
        );
      });
    }
    return false;
  };

  const getReservationType = (reservation) => {
    if (!reservation) return 'occupation';
    const statut = String(reservation.statut || reservation.status || '').toUpperCase();
    const type = String(reservation.type || reservation.type_reservation || '').toUpperCase();

    if (type.includes('RESERV') || statut.includes('RESERV') || statut.includes('ATTENTE') || statut.includes('PENDING')) {
      return 'reservation';
    }
    return 'occupation';
  };

  const getReservationsForDay = (chambre, jour) => {
    const dateCouranteStr = getDateString(jour);
    if (!chambre) return [];

    const chambreId = chambre.id;
    const chambreNumero = chambre.numero || chambre.nom;
    const resultats = [];

    reservations.forEach((reservation) => {
      if (!reservationConcerneChambre(reservation, chambreId, chambreNumero)) return;

      const dateArrivee = reservation.date_arrivee || reservation.date_debut || reservation.check_in;
      const dateDepart = reservation.date_depart || reservation.date_fin || reservation.check_out;

      if (!dateArrivee || !dateDepart) return;

      const dArrivee = formatDateLocal(dateArrivee);
      const dDepart = formatDateLocal(dateDepart);

      const estDansPeriode = dateCouranteStr >= dArrivee && dateCouranteStr < dDepart;
      const estJourArrivee = dateCouranteStr === dArrivee;
      const estJourDepart = dateCouranteStr === dDepart;

      if (estDansPeriode || estJourArrivee || estJourDepart) {
        resultats.push({
          ...reservation,
          _dateArrivee: dArrivee,
          _dateDepart: dDepart,
          _estJourArrivee: estJourArrivee,
          _estJourDepart: estJourDepart,
          _estOccupation: estDansPeriode,
          _type: getReservationType(reservation)
        });
      }
    });

    const uniques = [];
    const ids = new Set();

    resultats.forEach((item, index) => {
      const id = item.id || item.reservation_id || `${item.client_nom || item.client_id || 'client'}-${item._dateArrivee}-${item._dateDepart}-${index}`;
      if (!ids.has(String(id))) {
        ids.add(String(id));
        uniques.push(item);
      }
    });

    uniques.sort((a, b) => {
      const score = (item) => {
        if (item._estJourArrivee) return 1;
        if (item._type === 'reservation') return 2;
        if (item._estJourDepart) return 3;
        return 4;
      };
      return score(a) - score(b);
    });

    return uniques;
  };

  const handleCellClick = (chambre, jour) => {
    const events = getReservationsForDay(chambre, jour);
    if (events.length === 0) {
      setSelectedCell(null);
      return;
    }
    setSelectedCell({ chambre, jour, date: getDateString(jour), events });
    setSelectedDetailIndex(0);
  };

  const aujourdhuiDateObj = new Date();
  const estMoisCourant = aujourdhuiDateObj.getFullYear() === annee && aujourdhuiDateObj.getMonth() === mois;
  const jourCourantDuMois = aujourdhuiDateObj.getDate();
  const dateStatistique = estMoisCourant ? jourCourantDuMois : 1;

  const occupantsAujourdhui = useMemo(() => {
    return chambres
      .map((chambre) => {
        const events = getReservationsForDay(chambre, dateStatistique);
        const occupation = events.find((event) => event._estOccupation && event._type === 'occupation') ||
                           events.find((event) => event._estOccupation);
        return { chambre, resa: occupation, events };
      })
      .filter((item) => item.resa);
  }, [chambres, reservations, annee, mois, dateStatistique]);

  const totalChambres = chambres.length;
  const chambresOccupeesCount = occupantsAujourdhui.length;
  const tauxOccupationGlobal = totalChambres > 0 ? Math.round((chambresOccupeesCount / totalChambres) * 100) : 0;

  const getCellBackground = (events) => {
    if (!events || events.length === 0) return 'transparent';
    const contientReservation = events.some((event) => event._type === 'reservation');
    const contientOccupation = events.some((event) => event._type === 'occupation' && event._estOccupation);

    if (contientReservation && contientOccupation) return '#ede9fe';
    if (contientReservation) return '#fef3c7';
    if (contientOccupation) return '#dbeafe';
    return '#f1f5f9';
  };

  const getCellBorderColor = (events) => {
    if (!events || events.length === 0) return '#f1f5f9';
    const contientReservation = events.some((event) => event._type === 'reservation');
    const contientOccupation = events.some((event) => event._type === 'occupation' && event._estOccupation);

    if (contientReservation && contientOccupation) return '#8b5cf6';
    if (contientReservation) return '#f59e0b';
    if (contientOccupation) return '#2563eb';
    return '#cbd5e1';
  };

  const getClientName = (reservation) => {
    return (
      reservation?.client_nom ||
      reservation?.nom_client ||
      reservation?.client_name ||
      reservation?.client?.nom ||
      reservation?.client?.name ||
      reservation?.nom ||
      'Client non renseigné'
    );
  };

  const getEventLabel = (event) => {
    if (event._estJourArrivee) return 'CHECK-IN';
    if (event._estJourDepart) return 'CHECK-OUT';
    if (event._type === 'reservation') return 'RÉSERVATION';
    return 'OCCUPATION';
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f8fafc' }}>
      <Sidebar />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <header style={{ background: 'white', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <CalendarIcon size={22} color="#2563eb" />
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#1e293b' }}>
              CALENDRIER DES GÎTES & RÉSERVATIONS
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
              <button onClick={moisPrecedent} style={navBtnStyle} title="Mois précédent">
                <ChevronLeft size={18} />
              </button>
              <span style={{ padding: '0 16px', fontWeight: '900', fontSize: '14px', color: '#1e293b', minWidth: '140px', textAlign: 'center' }}>
                {nomsMois[mois]} {annee}
              </span>
              <button onClick={moisSuivant} style={navBtnStyle} title="Mois suivant">
                <ChevronRight size={18} />
              </button>
            </div>
            <button onClick={allerAujourdHui} style={{ padding: '8px 14px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '8px', fontWeight: '800', fontSize: '12px', cursor: 'pointer' }}>
              Aujourd'hui
            </button>
          </div>
        </header>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
            <div style={cardStatStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#64748b' }}>Taux d'Occupation</span>
                <Percent size={18} color="#2563eb" />
              </div>
              <div style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b', marginTop: '8px' }}>
                {tauxOccupationGlobal}%
              </div>
              <div style={{ fontSize: '11px', color: '#059669', fontWeight: '700', marginTop: '4px' }}>
                {chambresOccupeesCount} / {totalChambres} chambres occupées
              </div>
            </div>

            <div style={cardStatStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#64748b' }}>Chambres Libres</span>
                <Bed size={18} color="#059669" />
              </div>
              <div style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b', marginTop: '8px' }}>
                {Math.max(0, totalChambres - chambresOccupeesCount)}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', marginTop: '4px' }}>
                Disponibles immédiatement
              </div>
            </div>

            <div style={cardStatStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#64748b' }}>Total Réservations Mois</span>
                <CalendarCheck size={18} color="#d97706" />
              </div>
              <div style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b', marginTop: '8px' }}>
                {reservations.length}
              </div>
              <div style={{ fontSize: '11px', color: '#d97706', fontWeight: '700', marginTop: '4px' }}>
                Enregistrées sur {nomsMois[mois]}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'center', fontSize: '11px', color: '#64748b', fontWeight: '700' }}>
            <Legend background="#dbeafe" border="#93c5fd" label="Occupation" />
            <Legend background="#fef3c7" border="#f59e0b" label="Réservation" />
            <Legend background="#ede9fe" border="#8b5cf6" label="Chevauchement" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ArrowRight size={12} color="#059669" /> Check-in
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ArrowLeft size={12} color="#dc2626" /> Check-out
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#64748b', fontWeight: '700' }}>
              Chargement du calendrier d'occupation...
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '1100px', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ ...thStyle, minWidth: '220px', left: 0, zIndex: 3, background: '#f8fafc', position: 'sticky', borderRight: '2px solid #e2e8f0' }}>
                      Hébergement / Gîte
                    </th>
                    {joursMois.map((jour) => {
                      const dateObj = new Date(annee, mois, jour);
                      const nomJourSemaine = joursSemaine[dateObj.getDay()];
                      const estLeWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      const estAujourdHui = formatDateLocal(dateObj) === formatDateLocal(new Date());

                      return (
                        <th key={jour} style={{ ...thStyle, textAlign: 'center', minWidth: '40px', background: estAujourdHui ? '#dbeafe' : estLeWeekend ? '#f1f5f9' : '#f8fafc', color: estAujourdHui ? '#1d4ed8' : estLeWeekend ? '#dc2626' : '#475569' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700' }}>{nomJourSemaine}</div>
                          <div style={{ fontSize: '13px', fontWeight: '900', marginTop: '2px' }}>{jour}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {chambres.length === 0 ? (
                    <tr>
                      <td colSpan={nombreJours + 1} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                        Aucune chambre ou gîte configuré dans le système.
                      </td>
                    </tr>
                  ) : (
                    chambres.map((chambre) => (
                      <tr key={chambre.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...tdStyle, left: 0, zIndex: 2, background: 'white', position: 'sticky', fontWeight: '800', color: '#1e293b', borderRight: '2px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Bed size={15} color="#2563eb" />
                            <div>
                              <div>{chambre.numero || chambre.nom}</div>
                              <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 'normal' }}>
                                {chambre.type_chambre || chambre.capacite ? `${chambre.capacite || '2'} pers.` : ''}
                              </div>
                            </div>
                          </div>
                        </td>

                        {joursMois.map((jour) => {
                          const events = getReservationsForDay(chambre, jour);
                          const contientArrivee = events.some((event) => event._estJourArrivee);
                          const contientDepart = events.some((event) => event._estJourDepart);
                          const background = getCellBackground(events);

                          return (
                            <td
                              key={jour}
                              title={events.length > 0 ? `${events.length} événement(s) — Cliquez pour voir le détail` : ''}
                              onClick={() => handleCellClick(chambre, jour)}
                              style={{
                                ...tdStyle,
                                textAlign: 'center',
                                padding: '3px 2px',
                                background,
                                cursor: events.length > 0 ? 'pointer' : 'default',
                                position: 'relative',
                                minWidth: '40px',
                                height: '45px',
                                borderLeft: contientArrivee ? '2px solid #059669' : '1px solid #f1f5f9',
                                borderRight: contientDepart ? '2px solid #dc2626' : '1px solid #f1f5f9'
                              }}
                            >
                              {events.length === 0 ? (
                                <span style={{ color: '#cbd5e1', fontSize: '9px' }}>·</span>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', width: '100%' }}>
                                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                                    {contientArrivee && <ArrowRight size={11} color="#059669" />}
                                    {contientDepart && <ArrowLeft size={11} color="#dc2626" />}
                                  </div>
                                  <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                                    {events.slice(0, 4).map((event, index) => (
                                      <span
                                        key={index}
                                        style={{
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          background: event._type === 'reservation' ? '#f59e0b' : event._estJourDepart ? '#dc2626' : '#2563eb',
                                          display: 'inline-block'
                                        }}
                                      />
                                    ))}
                                  </div>
                                  {events.length > 1 && (
                                    <span style={{ fontSize: '8px', fontWeight: '900', color: '#7c3aed' }}>
                                      {events.length}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={16} color="#2563eb" />
              Occupants actuels / du jour ({occupantsAujourdhui.length})
            </h3>

            {occupantsAujourdhui.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                Aucune chambre occupée pour le moment aujourd'hui.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={recapThStyle}>Chambre / Gîte</th>
                      <th style={recapThStyle}>Client / Occupant</th>
                      <th style={recapThStyle}>Date d'Arrivée</th>
                      <th style={recapThStyle}>Date de Départ</th>
                      <th style={recapThStyle}>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {occupantsAujourdhui.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={recapTdStyle}>{item.chambre.numero || item.chambre.nom}</td>
                        <td style={{ ...recapTdStyle, color: '#334155' }}>{getClientName(item.resa)}</td>
                        <td style={{ ...recapTdStyle, color: '#059669', fontWeight: '700' }}>
                          {formatDateFR(item.resa?._dateArrivee || item.resa?.date_arrivee)}
                        </td>
                        <td style={{ ...recapTdStyle, color: '#dc2626', fontWeight: '700' }}>
                          {formatDateFR(item.resa?._dateDepart || item.resa?.date_depart)}
                        </td>
                        <td style={recapTdStyle}>
                          <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                            {item.resa?.statut || 'Occupé'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {selectedCell && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '20px' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedCell(null);
          }}
        >
          <div style={{ background: 'white', width: '420px', maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', border: '1px solid #cbd5e1', boxShadow: '0 20px 40px rgba(0,0,0,0.18)', borderRadius: '12px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '900', color: '#1e293b' }}>
                  Détails du planning
                </h4>
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#64748b' }}>
                  Chambre <strong>{selectedCell.chambre.numero || selectedCell.chambre.nom}</strong> — {formatDateFR(selectedCell.date)}
                </div>
              </div>
              <button
                onClick={() => setSelectedCell(null)}
                style={{ width: '32px', height: '32px', border: 'none', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={17} color="#475569" />
              </button>
            </div>

            {selectedCell.events.length > 1 && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#475569', marginBottom: '6px' }}>
                  Sélectionner l'événement
                </label>
                <select
                  value={selectedDetailIndex}
                  onChange={(e) => setSelectedDetailIndex(Number(e.target.value))}
                  style={{ width: '100%', padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', background: 'white', fontSize: '12px', fontWeight: '700', color: '#1e293b', outline: 'none', cursor: 'pointer' }}
                >
                  {selectedCell.events.map((event, index) => (
                    <option key={index} value={index}>
                      {getEventLabel(event)} — {getClientName(event)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '15px' }}>
              {selectedCell.events.map((event, index) => {
                const actif = index === selectedDetailIndex;
                const isReservation = event._type === 'reservation';

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDetailIndex(index)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: actif ? `2px solid ${isReservation ? '#f59e0b' : '#2563eb'}` : '1px solid #e2e8f0',
                      background: isReservation ? '#fffbeb' : '#eff6ff',
                      borderRadius: '8px',
                      padding: '9px 10px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '11px', color: isReservation ? '#b45309' : '#1d4ed8' }}>
                        {getEventLabel(event)}
                      </strong>
                      <span style={{ fontSize: '10px', color: '#64748b' }}>
                        {index + 1} / {selectedCell.events.length}
                      </span>
                    </div>
                    <div style={{ marginTop: '3px', fontSize: '11px', fontWeight: '700', color: '#334155' }}>
                      {getClientName(event)}
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedCell.events[selectedDetailIndex] && (
              <DetailReservation
                reservation={selectedCell.events[selectedDetailIndex]}
                getClientName={getClientName}
                getEventLabel={getEventLabel}
                formatDateFR={formatDateFR}
              />
            )}

            {selectedCell.events.length > 1 && (
              <div style={{ marginTop: '15px', padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#6d28d9', borderRadius: '7px', fontSize: '11px', fontWeight: '700' }}>
                ⚠️ Plusieurs événements sont associés à cette chambre pour cette date. Utilisez la liste déroulante ci-dessus pour consulter chaque personne.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailReservation = ({ reservation, getClientName, getEventLabel, formatDateFR }) => {
  const isReservation = reservation._type === 'reservation';

  return (
    <div style={{ border: `1px solid ${isReservation ? '#fcd34d' : '#bfdbfe'}`, background: isReservation ? '#fffbeb' : '#eff6ff', borderRadius: '10px', padding: '15px' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 9px', borderRadius: '5px', background: isReservation ? '#fef3c7' : '#dbeafe', color: isReservation ? '#b45309' : '#1d4ed8', fontSize: '10px', fontWeight: '900', marginBottom: '12px' }}>
        {isReservation ? <CalendarCheck size={12} /> : <Clock size={12} />}
        {getEventLabel(reservation)}
      </div>

      <div style={{ fontSize: '14px', fontWeight: '900', color: '#1e293b', marginBottom: '12px' }}>
        {getClientName(reservation)}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', fontSize: '12px', color: '#475569' }}>
        <div style={detailRowStyle}>
          <span><strong>Arrivée</strong></span>
          <span style={{ color: '#059669', fontWeight: '800' }}>
            {formatDateFR(reservation._dateArrivee || reservation.date_arrivee)}
            {reservation._estJourArrivee && <span style={{ marginLeft: '6px' }}>← Check-in</span>}
          </span>
        </div>

        <div style={detailRowStyle}>
          <span><strong>Départ</strong></span>
          <span style={{ color: '#dc2626', fontWeight: '800' }}>
            {formatDateFR(reservation._dateDepart || reservation.date_depart)}
            {reservation._estJourDepart && <span style={{ marginLeft: '6px' }}>← Check-out</span>}
          </span>
        </div>

        <div style={detailRowStyle}>
          <span><strong>Statut</strong></span>
          <span style={{ fontWeight: '800', color: isReservation ? '#b45309' : '#1d4ed8' }}>
            {reservation.statut || reservation.status || 'Occupé'}
          </span>
        </div>

        {reservation.nombre_personnes != null && (
          <div style={detailRowStyle}>
            <span><strong>Personnes</strong></span>
            <span>{reservation.nombre_personnes}</span>
          </div>
        )}

        {reservation.nombre_nuits != null && (
          <div style={detailRowStyle}>
            <span><strong>Nombre de nuits</strong></span>
            <span>{reservation.nombre_nuits}</span>
          </div>
        )}

        {reservation.telephone && (
          <div style={detailRowStyle}>
            <span><strong>Téléphone</strong></span>
            <span>{reservation.telephone}</span>
          </div>
        )}

        {reservation.commentaire && (
          <div style={{ marginTop: '4px', padding: '9px', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <strong>Commentaire :</strong>
            <div style={{ marginTop: '4px' }}>{reservation.commentaire}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const Legend = ({ background, border, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <span style={{ width: '14px', height: '14px', background, border: `1px solid ${border}`, borderRadius: '3px', display: 'inline-block' }} />
    {label}
  </div>
);

const thStyle = { padding: '10px 6px', fontSize: '11px', fontWeight: '900', color: '#475569', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px 6px', fontSize: '12px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
const navBtnStyle = { background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#334155' };
const cardStatStyle = { background: 'white', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.01)' };
const recapThStyle = { padding: '8px', color: '#475569', fontWeight: '800' };
const recapTdStyle = { padding: '8px', color: '#1e293b', fontWeight: '800' };
const detailRowStyle = { display: 'flex', justifyContent: 'space-between', gap: '15px', alignItems: 'center' };

export default PlanningOccupation;